import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Leaf, 
  Target, 
  Users,
  Building2,
  Award,
  ArrowRight,
  CheckCircle,
  Heart,
  Globe,
  Lightbulb,
  Handshake,
  TreePine,
  Recycle
} from "lucide-react";

export default function Presentation() {
  const missions = [
    {
      icon: Globe,
      title: "Impact Environnemental",
      description: "Réduire l'empreinte carbone et promouvoir des pratiques durables dans toutes nos actions"
    },
    {
      icon: Users,
      title: "Engagement Social",
      description: "Favoriser l'inclusion, le bien-être et le développement des communautés locales"
    },
    {
      icon: Building2,
      title: "Gouvernance Responsable",
      description: "Assurer transparence, éthique et responsabilité dans toutes nos décisions"
    },
    {
      icon: Lightbulb,
      title: "Innovation Durable",
      description: "Développer des solutions innovantes pour un avenir plus responsable"
    }
  ];

  const values = [
    { icon: Heart, title: "Engagement", desc: "Agir avec conviction pour un monde meilleur" },
    { icon: Handshake, title: "Collaboration", desc: "Travailler ensemble vers des objectifs communs" },
    { icon: TreePine, title: "Durabilité", desc: "Penser aux générations futures dans chaque décision" },
    { icon: Recycle, title: "Responsabilité", desc: "Assumer l'impact de nos actions sur la planète" }
  ];

  const achievements = [
    { value: "500+", label: "Entreprises accompagnées" },
    { value: "50K", label: "Décisions analysées" },
    { value: "75%", label: "Amélioration ESG moyenne" },
    { value: "2027", label: "Objectif CSRD" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost">Accueil</Button>
            </Link>
            <Link to="/actualites">
              <Button variant="ghost">Actualités</Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="rounded-full px-6">
                Commencer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="space-y-6 animate-fadeIn max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Award className="w-4 h-4" />
              Notre Engagement RSE
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight" style={{ fontFamily: 'Manrope' }}>
              VF ACTIONS
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Notre engagement pour un avenir durable. Découvrez comment nous accompagnons les organisations 
              dans leur transition écologique et sociale.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fadeIn">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
              Notre Mission
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Accompagner les organisations vers une performance durable et responsable
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {missions.map((mission, index) => (
              <Card 
                key={index} 
                className="card-hover border-0 bg-card animate-fadeIn"
                style={{ animationDelay: `${index * 0.15}s` }}
                data-testid={`mission-card-${index}`}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <mission.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                    {mission.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {mission.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 animate-fadeIn">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                Nos Valeurs
              </h2>
              <p className="text-lg text-muted-foreground">
                Chez VF ACTIONS, nous croyons que chaque décision compte. Nos valeurs guident 
                chacune de nos actions pour créer un impact positif durable.
              </p>
              <div className="space-y-4">
                {values.map((value, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-4 p-4 rounded-xl bg-card card-hover"
                    data-testid={`value-item-${index}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <value.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{value.title}</h4>
                      <p className="text-sm text-muted-foreground">{value.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative animate-fadeIn stagger-2">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-3xl" />
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHx0ZWFtJTIwY29sbGFib3JhdGlvbiUyMHN1c3RhaW5hYmlsaXR5fGVufDB8fHx8MTczNTY0MjAwMHww&ixlib=rb-4.1.0&q=85"
                alt="Team collaboration"
                className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-16 px-6 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((stat, index) => (
              <div 
                key={index} 
                className="text-center animate-fadeIn" 
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`achievement-${index}`}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
                  {stat.value}
                </div>
                <div className="text-sm opacity-80 uppercase tracking-wide">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-8 animate-fadeIn">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Notre Engagement CSRD 2027
            </h2>
            <p className="text-lg text-muted-foreground">
              En anticipation de la directive européenne CSRD, nous accompagnons les organisations 
              pour qu'elles soient prêtes et conformes aux nouvelles exigences de reporting en matière 
              de durabilité. Notre plateforme VF Decide intègre tous les indicateurs ESRS pour une 
              transition fluide vers ce nouveau cadre réglementaire.
            </p>
            <div className="grid md:grid-cols-3 gap-6 pt-8">
              {[
                { label: "Environnement (E)", items: ["ESRS E1 - Climat", "ESRS E2 - Pollution", "ESRS E3 - Eau", "ESRS E4 - Biodiversité", "ESRS E5 - Économie circulaire"] },
                { label: "Social (S)", items: ["ESRS S1 - Main-d'œuvre propre", "ESRS S2 - Chaîne de valeur", "ESRS S3 - Communautés", "ESRS S4 - Consommateurs"] },
                { label: "Gouvernance (G)", items: ["ESRS G1 - Conduite des affaires", "Éthique", "Transparence", "Gestion des risques"] }
              ].map((pillar, index) => (
                <Card key={index} className="border-0 bg-muted/50" data-testid={`csrd-pillar-${index}`}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
                      {pillar.label}
                    </h3>
                    <ul className="space-y-2">
                      {pillar.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-muted/30">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Rejoignez le mouvement VF ACTIONS
          </h2>
          <p className="text-lg text-muted-foreground">
            Ensemble, construisons un avenir plus durable. Commencez dès aujourd'hui à aligner 
            vos décisions avec vos objectifs RSE.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="rounded-full px-8 text-lg" data-testid="cta-join-btn">
                Rejoindre VF ACTIONS
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/actualites">
              <Button size="lg" variant="outline" className="rounded-full px-8 text-lg">
                Voir les actualités
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-card border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Leaf className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-foreground">VF Decide</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
            <Link to="/vf-actions" className="hover:text-primary transition-colors">VF ACTIONS</Link>
            <Link to="/actualites" className="hover:text-primary transition-colors">Actualités</Link>
          </div>
          <p className="text-sm text-muted-foreground">
            2026 VF Decide - Plateforme RSE/CSRD
          </p>
        </div>
      </footer>
    </div>
  );
}
